import React, { useState } from "react";
import TrebbleDropdown from "../common/TrebbleDropdown";
import "./styles.css";

interface Folder {
  id: string;
  name: string;
  color: string;
  itemCount: number;
}

interface FolderRowProps {
  folders: Folder[];
  onFolderClick: (folderId: string) => void;
  onNewFolder: () => void;
  onDeleteFolder?: (folder: Folder) => Promise<void>;
  onRenameFolder?: (folder: Folder) => void;
  onMoveFolder?: (folder: Folder) => void;
  ti18n?: Record<string, string>;
  canCreateFolder?: boolean;
  hideItemCount?: boolean;
}

const t = (ti18n: Record<string, string> | undefined, key: string): string => {
  if (ti18n) {
    return window.getI18n(ti18n, key);
  }
  return key;
};

const CARD_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  minWidth: 180,
  padding: "12px 18px",
  backgroundColor: "var(--bg-default, #fff)",
  border: "1px solid var(--border-default-color, #e0e0e4)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  transition: "box-shadow 0.15s ease, transform 0.15s ease",
  flexShrink: 0,
  position: "relative",
};

interface FolderCardProps {
  folder: Folder;
  onFolderClick: (folderId: string) => void;
  onDeleteFolder?: (folder: Folder) => Promise<void>;
  onRenameFolder?: (folder: Folder) => void;
  onMoveFolder?: (folder: Folder) => void;
  hideItemCount: boolean;
  ti18n?: Record<string, string>;
}

const FolderCard: React.FC<FolderCardProps> = ({
  folder,
  onFolderClick,
  onDeleteFolder,
  onRenameFolder,
  onMoveFolder,
  hideItemCount,
  ti18n,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const cardRef = React.useRef<HTMLDivElement>(null);

  const hasMenu = onDeleteFolder || onRenameFolder || onMoveFolder;

  const menuItems = {
    items: [
      ...(onRenameFolder
        ? [
            {
              key: "rename",
              label: t(ti18n, "FOLDER_RENAME_FOLDER"),
              icon: <span className="pe-7s-pen" style={{ fontSize: 16 }} />,
            },
          ]
        : []),
      ...(onMoveFolder
        ? [
            {
              key: "move",
              label: t(ti18n, "MOVE_TO_FOLDER"),
              icon: <span className="pe-7s-next-2" style={{ fontSize: 16 }} />,
            },
          ]
        : []),
      ...(onDeleteFolder
        ? [
            { type: "divider" as const },
            {
              key: "delete",
              label: t(ti18n, "DELETE"),
              icon: <span className="pe-7s-trash" style={{ fontSize: 16 }} />,
            },
          ]
        : []),
    ],
    onClick: ({ key }: { key: string }) => {
      setMenuOpen(false);
      if (key === "rename" && onRenameFolder) {
        onRenameFolder(folder);
      } else if (key === "move" && onMoveFolder) {
        onMoveFolder(folder);
      } else if (key === "delete" && onDeleteFolder) {
        onDeleteFolder(folder).catch(console.error);
      }
    },
  };

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={() => onFolderClick(folder.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFolderClick(folder.id); } }}
      className="library-folder-card"
      style={CARD_STYLE}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        if (!menuOpen) {
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
          e.currentTarget.style.transform = "none";
        }
      }}
    >
      <span
        className="pe-7s-folder"
        style={{
          fontSize: 20,
          color: folder.color,
          flexShrink: 0,
          WebkitTextStroke: "0.5px",
        }}
      />
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: "var(--text-default-color, #333)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {folder.name}
      </span>
      {!hideItemCount && (
        <span
          style={{
            fontSize: 11,
            color: "var(--text-muted, #aaa)",
            flexShrink: 0,
          }}
        >
          {folder.itemCount}
        </span>
      )}
      {hasMenu && (
        <div
          style={{ marginLeft: "auto", flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <TrebbleDropdown
            useEditorTheme={false}
            menu={menuItems}
            trigger={["click"]}
            open={menuOpen}
            onOpenChange={(open: boolean) => {
              setMenuOpen(open);
              if (!open && cardRef.current) {
                cardRef.current.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
                cardRef.current.style.transform = "none";
              }
            }}
            overlayClassName="library-card-dropdown"
          >
            <button
              type="button"
              className="library-folder-menu-btn"
              aria-label="Folder options"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <span className="pe-7s-more" />
            </button>
          </TrebbleDropdown>
        </div>
      )}
    </div>
  );
};

const FolderRow: React.FC<FolderRowProps> = ({
  folders,
  onFolderClick,
  onNewFolder,
  onDeleteFolder,
  onRenameFolder,
  onMoveFolder,
  ti18n,
  canCreateFolder = true,
  hideItemCount = false,
}) => {
  return (
    <div style={{ marginBottom: 32 }}>
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          paddingTop: 2,
          paddingBottom: 4,
        }}
      >
        {/* New Folder card */}
        {canCreateFolder && (
          <button
            type="button"
            onClick={onNewFolder}
            className="library-new-folder-card"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 180,
              flexShrink: 0,
              cursor: "pointer",
              padding: "12px 18px",
              fontSize: 13,
              fontWeight: 400,
              color: "var(--text-muted, #888)",
              backgroundColor: "var(--bg-default, #fff)",
              border: "1px dashed var(--border-default-color, #e0e0e4)",
              boxShadow: "none",
              transition: "box-shadow 0.15s ease, border-color 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <span className="pe-7s-folder" style={{ fontSize: 20, WebkitTextStroke: "0.5px" }} />
            {t(ti18n, "NEW_FOLDER")}
          </button>
        )}

        {/* Existing folders */}
        {folders.map((folder) => (
          <FolderCard
            key={folder.id}
            folder={folder}
            onFolderClick={onFolderClick}
            onDeleteFolder={onDeleteFolder}
            onRenameFolder={onRenameFolder}
            onMoveFolder={onMoveFolder}
            hideItemCount={hideItemCount}
            ti18n={ti18n}
          />
        ))}
      </div>
    </div>
  );
};

export default FolderRow;
